import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import instance from "../apiInstance";
import toast from "react-hot-toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faGavel,
  faArrowLeft,
  faClock,
  faUsers,
  faBell,
  faTimes,
  faLightbulb,
  faPaperPlane,
  faCamera,
  faTag,
  faExternalLinkAlt,
  faTrophy,
} from "@fortawesome/free-solid-svg-icons";
import "./AuctionListLux.css";
import { formatAuctionImageUrl, LUXURY_FALLBACK_IMAGE } from "../imageUrl";
import { isAdminEmail } from "../adminEmails";
import AuctionOffReminder from "../components/AuctionOffReminder";
import { useAuctionListTracker } from "../hooks/useAuctionListTracker";
import { trackPiece } from "../trackerBus";

const AuctionListLux = () => {
  const [auctions, setAuctions] = useState([]);
  const [endedToday, setEndedToday] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("active");
  const [isAdmin, setIsAdmin] = useState(false);
  const [showPwaBanner, setShowPwaBanner] = useState(false);
  const [showWishPopup, setShowWishPopup] = useState(false);
  const [wishCategory, setWishCategory] = useState("Bolsas");
  const [wishMessage, setWishMessage] = useState("");
  const [wishSent, setWishSent] = useState(false);
  const [wishSending, setWishSending] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [myUserId, setMyUserId] = useState(null);
  const navigate = useNavigate();
  const { trackList } = useAuctionListTracker(true);

  const CATEGORIES = ["Bolsas", "Sapatos", "Roupas", "Acessórios", "Joias", "Relógios", "Outros"];

  const handleSendWish = async () => {
    if (!wishMessage.trim()) return;
    setWishSending(true);
    try {
      await instance.post("/auctions/wish", { category: wishCategory, message: wishMessage.trim() });
      setWishSent(true);
      setWishMessage("");
    } catch {
      toast.error("Erro ao enviar sugestão");
    } finally {
      setWishSending(false);
    }
  };

  useEffect(() => {
    instance.get("/me")
      .then((res) => {
        setIsAdmin(isAdminEmail(res.data?.user?.email));
        setMyUserId(res.data?.user?.id || null);
      })
      .catch(() => setIsAdmin(false));
  }, []);

  useEffect(() => {
    const isStandalone = window.matchMedia("(display-mode: standalone)").matches
      || window.navigator.standalone === true;
    const dismissed = sessionStorage.getItem("pwa_banner_dismissed");
    if (!isStandalone && !dismissed) setShowPwaBanner(true);
  }, []);

  const fetchAuctions = useCallback(async () => {
    try {
      setLoading(true);

      let list = [];
      let endedList = [];

      if (filter === "active") {
        // /auctions/today devolve { active, ended_today } — 1 request pra ambos
        try {
          const response = await instance.get("/auctions/today");
          list = response.data?.active || [];
          endedList = response.data?.ended_today || [];
        } catch {
          // fallback pra API antiga se rota ainda não deployada
          const response = await instance.get("/auctions/active");
          list = response.data || [];
        }
      } else {
        const response = await instance.get("/auctions", { params: { status: filter } });
        list = response.data || [];
      }

      const withBids = await Promise.all(
        list.map(async (auc) => {
          try {
            const bidsRes = await instance.get(`/auctions/${auc._id || auc.id}/bids`);
            return { ...auc, recentBids: (bidsRes.data || []).slice(0, 3) };
          } catch {
            return { ...auc, recentBids: [] };
          }
        })
      );
      setAuctions(withBids);
      setEndedToday(endedList);

      if (filter === "active" && list.length === 0 && endedList.length === 0) {
        instance.get("/auctions", { params: { status: "pending" } })
          .then((res) => setPendingCount((res.data || []).length))
          .catch(() => setPendingCount(0));
      } else {
        setPendingCount(0);
      }
    } catch (error) {
      toast.error("Erro ao carregar leilões");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchAuctions();
  }, [fetchAuctions]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") fetchAuctions();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [fetchAuctions]);

  const formatTimeRemaining = (endTime) => {
    const now = new Date();
    const end = new Date(endTime);
    const diff = end - now;
    if (diff <= 0) return "Encerrado";
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `${days}d ${hours % 24}h`;
    }
    return `${hours}h ${minutes}min`;
  };

  const formatPrice = (price) =>
    new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(price);

  const openAuction = (auction) => {
    const aId = auction._id || auction.id;
    trackPiece("auction_card_click", {
      auctionId: aId,
      title: auction.title,
      price: auction.current_price,
    });
    trackList("auction_list_card_open", {
      auctionId: aId,
      title: auction.title,
      price: auction.current_price,
    });
    navigate(`/auction/${aId}`);
  };

  return (
    <div className="auc-lux-root">
      {/* Header */}
      <div className="auc-lux-header">
        <button
          className="auc-lux-back"
          aria-label="Voltar"
          onClick={() => navigate("/home")}
        >
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>
        <h2>
          <FontAwesomeIcon icon={faGavel} /> Leilões
        </h2>
      </div>

      {/* PWA Banner */}
      {showPwaBanner && (
        <div className="auc-lux-pwa-banner">
          <div className="auc-lux-pwa-icon">
            <FontAwesomeIcon icon={faBell} />
          </div>
          <div className="auc-lux-pwa-text">
            <strong>Ative as notificações!</strong>
            <span>Instale o app para receber alertas de novos leilões e lances em tempo real.</span>
          </div>
          <button
            className="auc-lux-pwa-close"
            onClick={() => {
              setShowPwaBanner(false);
              sessionStorage.setItem("pwa_banner_dismissed", "1");
            }}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      )}

      {/* Banner de sugestão inline */}
      {!wishSent ? (
        <button className="auc-lux-wish-inline" onClick={() => { setShowWishPopup(true); setWishSent(false); }}>
          <FontAwesomeIcon icon={faLightbulb} className="auc-lux-wish-inline-icon" />
          <span className="auc-lux-wish-inline-text">
            <strong>O que você quer ver no leilão?</strong>
            <small>Clique aqui e sugira a peça dos seus sonhos</small>
          </span>
          <FontAwesomeIcon icon={faPaperPlane} className="auc-lux-wish-inline-arrow" />
        </button>
      ) : (
        <div className="auc-lux-wish-inline sent">
          <span>✨ Sugestão enviada! Obrigada!</span>
        </div>
      )}

      {/* Filters — só admin vê Ativos / Em Breve / Encerrados */}
      {isAdmin && (
        <div className="auc-lux-filters">
          {[
            { key: "active", label: "Ativos" },
            { key: "pending", label: "Em Breve" },
            { key: "ended", label: "Encerrados" },
          ].map((f) => (
            <button
              key={f.key}
              className={`auc-lux-filter-btn ${filter === f.key ? "active" : ""}`}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="auc-lux-loading">
          <div className="auc-lux-spinner" />
          Carregando leilões...
        </div>
      ) : auctions.length === 0 && endedToday.length === 0 ? (
        filter === "active" ? (
          <>
            {isAdmin && pendingCount > 0 && (
              <div className="auc-lux-admin-hint">
                {pendingCount === 1
                  ? "1 leilão em Em Breve — abre automaticamente no horário programado."
                  : `${pendingCount} leilões em Em Breve — abrem automaticamente no horário.`}
                <button type="button" onClick={() => setFilter("pending")}>
                  Ver Em Breve
                </button>
              </div>
            )}
            <AuctionOffReminder />
          </>
        ) : (
          <div className="auc-lux-empty">
            {filter === "pending"
              ? "Nenhum leilão em breve"
              : "Nenhum leilão encerrado"}
          </div>
        )
      ) : (
        <>
        {auctions.length > 0 && (
          <div className="auc-lux-section">
            <div className="auc-lux-section-head">
              <span className="auc-lux-live-dot" />
              <h3>Ao Vivo</h3>
              <span className="auc-lux-section-count">{auctions.length}</span>
            </div>
        <div className="auc-lux-grid">
          {auctions.map((auction) => {
            const aId = auction._id || auction.id;
            return (
              <div
                key={aId}
                className="auc-lux-card"
                onClick={() => openAuction(auction)}
              >
                <div className="auc-lux-card-img">
                  <img
                    src={formatAuctionImageUrl(auction.images?.[0])}
                    alt={auction.title}
                    loading="lazy"
                    decoding="async"
                    onError={(e) => {
                      e.target.src = LUXURY_FALLBACK_IMAGE;
                    }}
                  />
                  <div className="auc-lux-badge">{auction.category}</div>
                  {auction.discount_percent > 0 && (
                    <div className="auc-lux-discount-badge">
                      <FontAwesomeIcon icon={faTag} /> -{auction.discount_percent}%
                    </div>
                  )}
                  {auction.images?.length > 1 && (
                    <div className="auc-lux-album-badge">
                      <FontAwesomeIcon icon={faCamera} />
                      <span>{auction.images.length} fotos</span>
                    </div>
                  )}
                </div>
                <div className="auc-lux-card-body">
                  <h3 className="auc-lux-card-title">{auction.title}</h3>
                  
                  {auction.store_price && (
                    <div className="auc-lux-store-compare">
                      <div className="auc-lux-store-line">
                        <span className="auc-lux-store-lbl">Loja Oficial:</span>
                        <span className="auc-lux-store-val">{formatPrice(auction.store_price)}</span>
                        {auction.store_price_usd && (
                          <span className="auc-lux-store-usd">(${Math.round(auction.store_price_usd).toLocaleString("en-US")} USD)</span>
                        )}
                      </div>
                      {auction.official_store_url && (
                        <a
                          href={auction.official_store_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="auc-lux-store-link"
                          onClick={(e) => e.stopPropagation()}
                          title="Ver na Loja Oficial"
                        >
                          <FontAwesomeIcon icon={faExternalLinkAlt} />
                        </a>
                      )}
                    </div>
                  )}

                  <div className="auc-lux-price-bar">
                    <span className="auc-lux-price-label">Lance Atual</span>
                    <span className="auc-lux-price-value">
                      {formatPrice(auction.current_price)}
                    </span>
                  </div>
                  <div className="auc-lux-meta">
                    <div className="auc-lux-meta-item">
                      <FontAwesomeIcon icon={faClock} />
                      <span>{formatTimeRemaining(auction.end_time)}</span>
                    </div>
                    <div className="auc-lux-meta-item">
                      <FontAwesomeIcon icon={faUsers} />
                      <span>{auction.total_bids} lances</span>
                    </div>
                  </div>
                  {auction.recentBids?.length > 0 && (
                    <div className="auc-lux-recent-bids">
                      {auction.recentBids.map((bid, idx) => {
                        const isSelf = Boolean(bid.is_self || bid.user_name === "Você");
                        return (
                          <div key={bid.id || idx} className="auc-lux-bid-row">
                            <span className={`auc-lux-bid-name ${isSelf ? "self-name" : ""}`}>
                              {idx === 0 && "🏆 "}{isSelf ? "Você" : bid.user_name}
                            </span>
                            <span className="auc-lux-bid-amount">
                              {formatPrice(bid.amount)}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <button className="auc-lux-card-btn">Dar Lance</button>
                </div>
              </div>
            );
          })}
        </div>
          </div>
        )}

        {endedToday.length > 0 && (
          <div className="auc-lux-section auc-lux-section--ended">
            <div className="auc-lux-section-head">
              <FontAwesomeIcon icon={faTrophy} className="auc-lux-section-icon" />
              <h3>Encerrados hoje</h3>
              <span className="auc-lux-section-count">{endedToday.length}</span>
            </div>
            <div className="auc-lux-grid">
              {endedToday.map((auction) => {
                const aId = auction._id || auction.id;
                const youWon = myUserId && auction.winner_id && String(auction.winner_id) === String(myUserId);
                return (
                  <div
                    key={aId}
                    className={`auc-lux-card auc-lux-card--ended ${youWon ? "auc-lux-card--you-won" : ""}`}
                    onClick={() => openAuction(auction)}
                  >
                    <div className="auc-lux-card-img">
                      <img
                        src={formatAuctionImageUrl(auction.images?.[0])}
                        alt={auction.title}
                        loading="lazy"
                        decoding="async"
                        onError={(e) => { e.target.src = LUXURY_FALLBACK_IMAGE; }}
                      />
                      <div className="auc-lux-ended-overlay">
                        {youWon ? "VOCÊ ARREMATOU" : "LEILÃO ENCERRADO"}
                      </div>
                    </div>
                    <div className="auc-lux-card-body">
                      <h3 className="auc-lux-card-title">{auction.title}</h3>
                      <div className="auc-lux-ended-info">
                        <div className="auc-lux-ended-winner">
                          <FontAwesomeIcon icon={faTrophy} />
                          <span>{youWon ? "Você levou" : `${auction.winner_name || "O maior lance"} levou`}</span>
                        </div>
                        <div className="auc-lux-ended-amount">
                          Fechou em <strong>{formatPrice(auction.winner_amount || auction.current_price)}</strong>
                        </div>
                      </div>
                      {auction.store_price && (
                        <div className="auc-lux-store-compare">
                          <div className="auc-lux-store-line">
                            <span className="auc-lux-store-lbl">Loja:</span>
                            <span className="auc-lux-store-val">{formatPrice(auction.store_price)}</span>
                          </div>
                        </div>
                      )}
                      <button className="auc-lux-card-btn auc-lux-card-btn--ghost">Ver leilão</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        </>
      )}

      {/* Popup de sugestão */}
      {showWishPopup && (
        <div className="auc-lux-wish-overlay" onClick={() => setShowWishPopup(false)}>
          <div className="auc-lux-wish-popup" onClick={(e) => e.stopPropagation()}>
            <button className="auc-lux-wish-close" onClick={() => setShowWishPopup(false)}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
            {wishSent ? (
              <div className="auc-lux-wish-success">
                <div className="auc-lux-wish-success-icon">✨</div>
                <h3>Obrigada pela sugestão!</h3>
                <p>Vamos analisar e trazer para o próximo leilão.</p>
                <button className="auc-lux-wish-btn" onClick={() => setShowWishPopup(false)}>Fechar</button>
              </div>
            ) : (
              <>
                <div className="auc-lux-wish-header">
                  <FontAwesomeIcon icon={faLightbulb} className="auc-lux-wish-header-icon" />
                  <h3>O que você quer ver no leilão?</h3>
                  <p>Conte pra gente qual peça ou marca você sonha em arrematar!</p>
                </div>
                <div className="auc-lux-wish-form">
                  <label>Categoria</label>
                  <select value={wishCategory} onChange={(e) => setWishCategory(e.target.value)}>
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <label>Sua sugestão</label>
                  <textarea
                    value={wishMessage}
                    onChange={(e) => setWishMessage(e.target.value)}
                    placeholder="Ex: Bolsa Chanel Classic Flap, Tênis Louis Vuitton Trainer..."
                    rows={3}
                    maxLength={300}
                  />
                  <span className="auc-lux-wish-chars">{wishMessage.length}/300</span>
                  <button
                    className="auc-lux-wish-btn"
                    disabled={!wishMessage.trim() || wishSending}
                    onClick={handleSendWish}
                  >
                    {wishSending ? "Enviando..." : <><FontAwesomeIcon icon={faPaperPlane} /> Enviar Sugestão</>}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AuctionListLux;
